import React, { useCallback, useEffect, useRef, useState } from "react";
import { ColumnApi, GridApi, GridReadyEvent } from "ag-grid-community";
import { AgGridColumn, AgGridReact } from "ag-grid-react";
import { Button } from "@material-ui/core";
import ImportExportIcon from "@material-ui/icons/ImportExport";

import { Member } from "../../../../../../../../../../@types/member.types";
import { memberLogsProps } from "../../../../../../../../../../@types/components/memberActivityProps.types";
import { MemberLogData } from "../../../../../../../../../../@types/components/memberLogProps.types";
import timeAgo from "../../../../../../../../../../utils/commonFunctions/dateDifferenceCalculator";
import { dateTimeZoneConverter } from "../../../../../../../../../../utils/commonFunctions/date";
import getAgGridHeight from "../../../../../../../../../../utils/commonFunctions/getAgGridHeight";
import {
	handleDateCellRenderer,
	handleNameCellRenderer,
	handleValueCellRenderer
} from "../../../../../../../../../../utils/commonFunctions/cellRenderers";

import "./dependentLogGrid.css";

const DependentLogGrid: React.FC<memberLogsProps> = ({ memberLogs }): JSX.Element => {
	const [gridHeight, setGridHeight] = useState<string>("400px");
	const [agGridAPI, setAgGridAPI] = useState<GridApi | null>(null);
	const [isReadyForExport, setIsReadyForExport] = useState<boolean>(false);
	const [agGridColumnAPI, setAgGridColumnAPI] = useState<ColumnApi | null>(null);

	const agGridRef = useRef<any>(null);

	const handleAgGridReady = (params: GridReadyEvent) => {
		const { api, columnApi } = params;
		setAgGridAPI(Object.assign({}, agGridAPI, api));
		setAgGridColumnAPI(columnApi);
	};

	const handleFirstDataRendered = (params: any) => {
		params.api.sizeColumnsToFit();
		params.columnApi.setColumnWidth("action", "100%", true);
		const { api, columnApi } = params;
		api.sizeColumnsToFit();
	};

	const handleStatusValueCellRenderer = ({ value }: { value: MemberLogData["status"] }) => {
		return (
			<div className="status-chip-container">
				<div
					className={
						value === "PASS"
							? "chip success-status-chip"
							: value === "FAIL"
							? "chip failure-status-chip"
							: "chip"
					}
				>
					<div className="text">{value}</div>
				</div>
			</div>
		);
	};

	const handleTimeCellRenderer = ({ value }: { value: string }) => {
		return <div>{timeAgo(value as string) as string}</div>;
	};

	const handleExportButtonClick = useCallback(() => {
		agGridRef?.current?.api.exportDataAsCsv({
			fileName: "Member Activity Logs.csv",
			processCellCallback: (params: any) => {
				// console.log("params", params);
				switch (params.column.colId) {
					case "employee": {
						return params.value.first_name + " " + params.value.last_name;
					}
					case "enrollment_date": {
						return dateTimeZoneConverter(params.value) as string;
					}
					default:
						return params.value;
				}
			},
			columnKeys: ["activity", "status", "time", "date", "activity_initiated_by", "description"]
		});
	}, []);

	useEffect(() => {
		const _gridHeight = getAgGridHeight(memberLogs.length);
		if (memberLogs.length !== 0) {
			setIsReadyForExport(true);
		}
		setGridHeight(_gridHeight);
	}, [memberLogs]);

	return (
		<>
			<div className="member-logs-grid-container glb-tab-grid-container" id="member-logs-grid-container">
				<div className="ag-theme-alpine" style={{ height: gridHeight, width: "100%" }}>
					<AgGridReact
						ref={agGridRef}
						rowData={memberLogs}
						onGridReady={handleAgGridReady}
						onFirstDataRendered={handleFirstDataRendered}
						rowSelection={"Single"}
						animateRows
						defaultColDef={{
							filter: "agTextColumnFilter",
							floatingFilter: true,
							resizable: true
						}}
						frameworkComponents={{
							handleDateCellRenderer: handleDateCellRenderer,
							handleTimeCellRenderer: handleTimeCellRenderer,
							handleNameCellRenderer: handleNameCellRenderer,
							handleValueCellRenderer: handleValueCellRenderer,
							handleStatusValueCellRenderer: handleStatusValueCellRenderer
						}}
					>
						<AgGridColumn
							field="activity"
							headerName="Activity"
							floatingFilterComponentParams={{ suppressFilterButton: true }}
							cellRenderer="handleValueCellRenderer"
							suppressSizeToFit
						></AgGridColumn>
						<AgGridColumn
							field="status"
							headerName="Status"
							floatingFilterComponentParams={{ suppressFilterButton: true }}
							cellRenderer="handleStatusValueCellRenderer"
							suppressSizeToFit
						></AgGridColumn>
						<AgGridColumn
							field="time"
							headerName="Time"
							floatingFilterComponentParams={{ suppressFilterButton: true }}
							cellRenderer="handleTimeCellRenderer"
							suppressSizeToFit
						></AgGridColumn>
						<AgGridColumn
							field="date"
							headerName="Date"
							floatingFilterComponentParams={{ suppressFilterButton: true }}
							cellRenderer="handleDateCellRenderer"
							suppressSizeToFit
						></AgGridColumn>
						<AgGridColumn
							field="activity_initiated_by"
							headerName="Activity Initiated By"
							floatingFilterComponentParams={{ suppressFilterButton: true }}
							cellRenderer="handleValueCellRenderer"
							suppressSizeToFit
						></AgGridColumn>
						<AgGridColumn
							field="description"
							headerName="Log Description"
							floatingFilterComponentParams={{ suppressFilterButton: true }}
							cellRenderer="handleValueCellRenderer"
						></AgGridColumn>
					</AgGridReact>
				</div>
			</div>
			{isReadyForExport ? (
				<div className="pf-action-button-container mg-top-20 mg-right-ve10" id="pf-action-button-container">
					<Button className="button-green" variant="contained" onClick={handleExportButtonClick}>
						<span className="button-label-with-icon">Export</span>
						<span>
							<ImportExportIcon className="button-icon" />
						</span>
					</Button>
				</div>
			) : null}
		</>
	);
};

export default DependentLogGrid;
